import React, {FunctionComponent} from 'react';
import * as d3 from 'd3';

// MARK:- Helper Functions
function getColorOfText(percent: number)
{
  if (percent < 64.5) {
    return '#f56757'; //'#ef4937'
  }

  if (percent < 74.5) {
    return '#ffcf24'; //gold
  }

  if (percent < 80.0) {
    return '#8aaf74'; //green
  }

  return '#8aaf74';
}

// Create styles
const styles = {
  chart: {
    fontSize: 11,
    fontWeight: 700,
  }
};

const AssessmentsBar: FunctionComponent<any> = ({ xScale, yScale, data, xOffset, yOffset }) => {
  const { title, metric } = data;
  const width: number = xScale(metric);
  const height: number = yScale.bandwidth() / 2;
  const x = xScale(0);
  const y = (yScale(title) ?? 0);
  const path=`M${x + xOffset},${y + yOffset + height / 2} h${width - 8} a8,8 0 0 1 8,8 v${height - 8 -9} a8,8 0 0 1 -8,8 h${8 - width} z`;

  return (
    <g>
      { width && <path d={path} fill={getColorOfText(metric)} /> }
      <rect x={x} y={y + yOffset + height / 2} width={xOffset} height={height} fill='#eff2f4'/>
      <text style={styles.chart} x={x + 5} y={y + yOffset + height + 5} fill='#194261'>
        {title}
      </text>
      <text style={styles.chart} x={x + xOffset + (width > 0 ? width / 2 - 10 : 5) } y={y + yOffset + height + 5} fill='#3a3a3a'>
        {metric.toFixed(2) + '%'}
      </text>
    </g>
  );
}

const AssessmentChart: FunctionComponent<any> = ({ data }: { data: Array<any> }) => {
  const width = 800;
  const height = 500;
  const lblWidth = 200;
  const titleHeight = 50;
  const footerHeight = 100;
  //const radius = 8;

  const xScale = d3.scaleLinear()
    .domain([0, 100]) //TODO: calculate or take as part of properties
    .range([0, width - lblWidth]);
  const yValues = data.map(x => x.title);
  const yScale = d3.scaleBand()
    .domain(yValues)
    .range([0, height - (titleHeight + footerHeight)]); //invert arguments to render an inverted list of ticks (domain)

  const fx = (index: number): number => 200 * index;
  const xTickPosition = (x: number): number => {
    const pos = xScale(x) - 5;
    if (x === 100) return pos - 30;
    return pos;
  }

  const headerPath = `M0,0 h${width - 8} a8,8 0 0 1 8,8 v${titleHeight - 8} h${-width} v${8 - titleHeight} a8,8 0 0 1 8,-8 z`;

  return (
    <svg viewBox='0 0 800 600'>
      <g>
        <rect
          x={xScale(64.5) + lblWidth}
          y={titleHeight}
          width={xScale(74.5) - xScale(64.5)}
          height={height - titleHeight - footerHeight}
          fill='#ffcf24'
          opacity='0.4'
        />
        <rect
          x={xScale(74.5) + lblWidth}
          y={titleHeight}
          width={xScale(100) - xScale(74.5)}
          height={height - titleHeight - footerHeight}
          fill='#8aaf74'
          opacity='0.4'
        />

        <path d={headerPath} fill='#194261'/>
        <text x={20} y={25} fill='#ffcf24' style={styles.chart}>
          Focus Areas
        </text>

        <g>
          {d3.ticks(0, 100, 10).map(
            index => <text
                key={index}
                style={styles.chart}
                x={xTickPosition(index) + lblWidth}
                y={25}
                fill='#ffcf24'
              >
                {`${index}%`}
              </text>
          )}
        </g>

        <g>
          {data.map(d => <AssessmentsBar key={d.value} xScale={xScale} yScale={yScale} data={d} xOffset={lblWidth} yOffset={titleHeight} />)}
        </g>

        {
          [
            { value:  0.0, label: '< 64.5%', },
            { value: 64.5, label: '64.5% - 74.5%', },
            { value: 74.5, label: '> 74.5%', },
            { value: 80.0, label: '> 80.0% - World Class', },
          ].map(({value, label}, index) => {
            return (
              <g key={index}>
                <rect
                  x={fx(index)}
                  y={height - footerHeight / 2 - 15}
                  width={20}
                  height={20}
                  rx={4}
                  ry={4}
                  fill={getColorOfText(value)}
                />
                <text
                  x={fx(index) + 28}
                  y={height - footerHeight / 2}
                  fill='#194261'
                  style={styles.chart}
                >
                  {label}
                </text>
              </g>
            );
          })
        }

        <g>
          <line
            x1={lblWidth + xScale(80.0)}
            y1={titleHeight + 1}
            x2={lblWidth + xScale(80.0)}
            y2={height - footerHeight}
            strokeWidth={1}
            stroke="rgba(0, 0, 0, 0.2)"
            strokeDasharray="5,3"
          />
        </g>
      </g>
    </svg>
  );
}

export {
  AssessmentChart,
  getColorOfText
};
